import { h } from '@stencil/core';
import state from 'store';
import { i18n } from 'i18n';
import { triggerTrackEvent, triggerOpenNewLink } from 'utils/events';
import distributionModal, { updateDistributionModal } from 'store/distributionModal';
import EvaMessage from 'eva-components/Message';
import { parseDistributionObjToStr, updateActionDistribution } from 'utils/utils';
import ActionButton from 'eva-components/ActionButton';

export default function DistributionModal({ show }: { show: boolean }) {
  const isWaitingBackend = distributionModal.status === 'WAITING_BACKEND',
    adults = {
      disableDown: distributionModal.adults === 1 || isWaitingBackend,
    },
    minors = {
      disableDown: distributionModal.minors === 0 || isWaitingBackend,
    },
    disableUp = distributionModal.minors + distributionModal.adults >= 8 || isWaitingBackend,
    isInvalidForm = distributionModal.ageMinors.some(age => age === '-1');

  const onClose = () => {
    state.showFullScreenModal = null;
  };

  const updateDistribution = () => {
    const parsedDistribution = parseDistributionObjToStr(distributionModal.adults, distributionModal.ageMinors);
    //Update filters in state
    state.filters = state.filters.some(filter => filter.id === 'distribution')
      ? state.filters.map(filter =>
          filter.id === 'distribution'
            ? {
                ...filter,
                description: `distribution: ${parsedDistribution}`,
                value: {
                  ...filter.value,
                  value: parseDistributionObjToStr(distributionModal.adults, distributionModal.ageMinors),
                },
              }
            : filter,
        )
      : state.filters.concat({
          id: 'distribution',
          name: 'Passengers',
          description: `distribution: ${parsedDistribution}`,
          value: {
            type: 'AttributeAPI',
            id: 'distribution',
            value: parsedDistribution,
          },
          products: ['ALL'],
          removeFilterPrompt: '',
        });

    //Update action params
    updateDistributionModal({ action: updateActionDistribution(distributionModal.adults, distributionModal.ageMinors, distributionModal.action) });
  };

  const changeDistribution = (propType: 'minors' | 'adults', method: 'increase' | 'decrease') => {
    triggerTrackEvent({
      event_type: 'user',
      event_name: `click_change_distribution_${propType}`,
      data: {
        action: method,
      },
    });

    updateDistributionModal({
      [propType]: method === 'decrease' ? distributionModal[propType] - 1 : distributionModal[propType] + 1,
      ageMinors: propType === 'minors' ? (method === 'decrease' ? distributionModal.ageMinors.slice(0, -1) : [...distributionModal.ageMinors, '10']) : distributionModal.ageMinors,
    });
    updateDistribution();
  };

  const changeAgeMinor = (index: number, eventTarget: any) => {
    triggerTrackEvent({
      event_type: 'user',
      event_name: `click_change_minor_age`,
    });

    updateDistributionModal({
      ageMinors: distributionModal.ageMinors.map((age, i) => (i === index ? eventTarget.value : age)),
    });
    updateDistribution();
  };

  const getAgeDescription = (ageAsString: string): string | null => {
    if (ageAsString !== '-1') {
      const age = Number(ageAsString);
      if (age <= 1) {
        return fare1Texts[distributionModal.product];
      } else if (age >= 2 && age <= 11) {
        return fare2Texts[distributionModal.product];
      } else {
        return fare3Texts[distributionModal.product];
      }
    }
    return null;
  };

  const onSubmit = () => {
    if (distributionModal.product === 'hotels') {
      onSubmitHotels();
    }
  };

  const nextTexts = {
    hotels: i18n.t.boards.accommodationOffers.distributionModal.next,
    flights: i18n.t.boards.flightOffers.distributionModal.next,
  };

  const errorTexts = {
    hotels: i18n.t.distributionModal.error,
    flights: i18n.t.boards.flightOffers.distributionModal.error,
  };

  const distributionErrorTexts = {
    hotels: i18n.t.distributionModal.errorDistribution,
    flights: i18n.t.boards.flightOffers.distributionModal.errorDistribution,
  };

  const fare1Texts = {
    hotels: null,
    flights: i18n.t.boards.flightOffers.distributionModal.minors.fare1,
  };
  const fare2Texts = {
    hotels: null,
    flights: i18n.t.boards.flightOffers.distributionModal.minors.fare2,
  };
  const fare3Texts = {
    hotels: null,
    flights: i18n.t.boards.flightOffers.distributionModal.minors.fare3,
  };

  const onSubmitHotels = () => {
    const accommodationId = distributionModal.productId;
    const checkin = distributionModal.departureDate;
    const checkout = distributionModal.returnDate;
    const queryParams = distributionModal.extraData.queryParams;
    const partialUri = `/accommodations/detail/${accommodationId}/${checkin}/${checkout}`;
    triggerTrackEvent({
      event_type: 'user',
      event_name: 'click_see_detail_passenger_hotels',
      data: {
        adults: distributionModal.adults.toString(),
        minors: distributionModal.minors.toString(),
      },
    });
    state.showFullScreenModal = null;
    const distribution = parseDistributionObjToStr(distributionModal.adults, distributionModal.ageMinors);
    const newUrl = `${partialUri}/${distribution}?${queryParams}`;

    triggerOpenNewLink({
      newTab: true,
      href: newUrl,
    });
  };

  return show ? (
    <div id={'distribution-modal'} role="presentation" class={'eva-3-modal -sm -show-sticky-footer -smooth-scroll fullscreen-modal -show-modal'}>
      <div class="modal-header" role="presentation">
        <div class="modal-title-container">
          <h4 class="eva-3-h4 modal-title">{i18n.t.distributionModal.title}</h4>
        </div>
        <i
          class={{
            'eva-3-icon-cross modal-close -eva-3-mr-md': true,
            'hide': distributionModal.status === 'WAITING_BACKEND',
          }}
          role="button"
          aria-label={i18n.t.distributionModal.close}
          onClick={() => {
            triggerTrackEvent({ event_type: 'user', event_name: `click_close_passenger_${distributionModal.product}` });
            onClose();
          }}
        ></i>
      </div>
      <div class="modal-content" role="presentation">
        {distributionModal.status === 'ERROR' && (
          <EvaMessage
            type={'ERROR'}
            description={distributionModal.errorStatus === 410 ? distributionErrorTexts[distributionModal.product] : errorTexts[distributionModal.product]}
            className={'error-message'}
          />
        )}

        <div class={'passenger-container'}>
          <div class={'description'}>
            <p class={'eva-3-h5'}>{i18n.t.distributionModal.adults.title}</p>
            <p class={'eva-3-body-2'}>{i18n.t.distributionModal.adults.description}</p>
          </div>
          <div class={'stepper-container'}>
            <div class="eva-3-steppers -md">
              <input class="steppers-tag" readonly value={distributionModal.adults} />
              <button class="steppers-icon-left eva-3-icon-minus" disabled={adults.disableDown} onClick={() => changeDistribution('adults', 'decrease')}></button>
              <button disabled={disableUp} class="steppers-icon-right eva-3-icon-plus" onClick={() => changeDistribution('adults', 'increase')}></button>
            </div>
          </div>
        </div>
        <div class={'passenger-container'}>
          <div class={'description'}>
            <p class={'eva-3-h5'}>{i18n.t.distributionModal.minors.title}</p>
            <p class={'eva-3-body-2'}>{i18n.t.distributionModal.minors.description}</p>
          </div>
          <div class={'stepper-container'}>
            <div class="eva-3-steppers -md">
              <input class="steppers-tag" readonly value={distributionModal.minors} />
              <button class="steppers-icon-left eva-3-icon-minus" disabled={minors.disableDown} onClick={() => changeDistribution('minors', 'decrease')}></button>
              <button disabled={disableUp} class="steppers-icon-right eva-3-icon-plus" onClick={() => changeDistribution('minors', 'increase')}></button>
            </div>
          </div>
        </div>

        {new Array(distributionModal.minors).fill(0).map((number, indexMinor) => {
          const selectedValue = distributionModal.ageMinors[indexMinor];
          const ageDescription = getAgeDescription(selectedValue);
          return (
            <div class={'passenger-container'}>
              <div class={'description'}>
                <p class={'eva-3-h5'}>
                  {i18n.t.distributionModal.minors.ageTitle} {indexMinor + 1}
                </p>
                <p class={'eva-3-body-2'}>{i18n.t.distributionModal.minors.ageDescription}</p>
              </div>
              <div class={'age-container'}>
                <div class="eva-3-select -md">
                  <div class="select-container">
                    <select class="select-tag" onInput={event => changeAgeMinor(indexMinor, event.currentTarget)}>
                      <option class="select-option" value="-1" selected={selectedValue === '-1'}>
                        {i18n.t.distributionModal.minors.labelSelect}
                      </option>
                      {new Array(18).fill(0).map((number, indexAge) => (
                        <option class="select-option" value={indexAge} selected={selectedValue === indexAge.toString(10)}>
                          {indexAge === 0 ? i18n.t.distributionModal.minors.underOneAge : indexAge}
                        </option>
                      ))}
                    </select>
                  </div>
                  {ageDescription !== null && <p class={'eva-3-p fare-description'}>{ageDescription}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div class="modal-footer">
        {typeof distributionModal.action === 'undefined' ? (
          <button
            type="button"
            class={{
              'eva-3-btn -lg -primary': true,
              '-loading': distributionModal.status === 'WAITING_BACKEND',
            }}
            disabled={isInvalidForm}
            onClick={onSubmit}
          >
            <em class="btn-text">{nextTexts[distributionModal.product]}</em>
            <span class="btn-status">{i18n.t.boards.loadingTexts.loading}</span>
          </button>
        ) : (
          <ActionButton action={distributionModal.action} doAfter={onClose} disabled={isInvalidForm} />
        )}
      </div>
    </div>
  ) : null;
}
